import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Flex,
  Group,
  Button,
  Menu,
  Table,
  Text,
  Badge,
  Loader,
  Paper,
  TextInput,
  Select,
  Checkbox,
  Alert,
} from "@mantine/core";
import {
  clearTenantDefaultSavedItem,
  deleteSavedItem,
  markSavedItemRun,
  publishSavedItem,
  setSavedFavorite,
  setTenantDefaultSavedItem,
  unpublishSavedItem,
  updateSavedItem,
  setPersonalMenuAssignment,
  getAdminTenantHeader,
} from "../services/saved.ts";
import { readFilterMemory, writeFilterMemory } from "../services/filterMemory.ts";

export type SavedItem = {
  id: number;
  user_id?: number;
  kind: "card" | "dashboard";
  metabase_id: number;
  title: string;
  display_name?: string | null;
  is_favorite?: boolean;
  personal_menu_section?: string | null;
  has_personal_menu_assignment?: boolean;
  published_menu_section?: string | null;
  personal_menu_sort_order?: number | null;
  show_in_personal_menu?: boolean;
  last_run_at?: string | null;
  favorite_group_id?: number | null;
  favorite_group_name?: string | null;
  can_edit_saved?: boolean;
  is_published?: boolean;
  is_tenant_default?: boolean;
  created_by_email?: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  mb?: {
    exists: boolean | null;
    name?: string | null;
    updated_at?: string | null;
    archived?: boolean;
    error?: string;
  };
};

type PublishedReport = {
  id: number;
  slug: string;
  title: string;
  mb_type: "card" | "dashboard";
  mb_resource_id: number;
  menu_section?: string | null;
  refresh_sec?: number | null;
  public_url?: string | null;
};

type PublishedPreview = {
  tenant: { id: number; code: string; name: string };
  role: { id: number; code: string; name: string };
  menu: any[];
};

type Props = {
  apiBase: string;
  accessToken: string;
  email?: string | null;
  onNewQuestion: (sourceMode?: "model" | "sql") => void;
  onNewDashboard: () => void;
  canCreateSql?: boolean;
  onOpen: (item: {
    id: number;
    kind: "card" | "dashboard";
    metabase_id: number;
    title?: string;
    can_edit_saved?: boolean;
  }) => void;
  onPublished?: (options?: {
    openDefault?: boolean;
    targetKey?: string;
    publishedReport?: PublishedReport;
    preview?: PublishedPreview | null;
  }) => Promise<void> | void;
  menuTitles?: string[];
  defaultMenuItemKey?: string | null;
  onSetDefault?: (key: string) => Promise<void> | void;
  onClearDefault?: () => Promise<void> | void;
  administrativeTenantLabel?: string | null;
  onExitAdministrativeTenant?: () => void;
};

export default function MyDataSavedBrowser({
  apiBase,
  accessToken,
  email,
  onNewQuestion,
  onNewDashboard,
  canCreateSql = false,
  onOpen,
  onPublished,
  menuTitles = [],
  defaultMenuItemKey,
  onSetDefault,
  onClearDefault,
  administrativeTenantLabel,
  onExitAdministrativeTenant,
}: Props) {
  const userFilterKey = email || "anonymous";
  const rememberedBrowserFilters =
    readFilterMemory(userFilterKey, "saved-browser")?.savedBrowser || {};

  const [items, setItems] = useState<SavedItem[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unpublishWarning, setUnpublishWarning] = useState<string | null>(null);
  const [query, setQuery] = useState(rememberedBrowserFilters.query || "");
  const [kind, setKind] = useState<string | null>(rememberedBrowserFilters.kind || "all");
  const [sort, setSort] = useState<string | null>(rememberedBrowserFilters.sort || "updated_at");
  const [direction, setDirection] = useState<string | null>(rememberedBrowserFilters.direction || "desc");
  const [scope, setScope] = useState<string | null>("all");
  const [favoriteFilter, setFavoriteFilter] = useState<string | null>(rememberedBrowserFilters.favoriteFilter || "all");
  const [reloadTick, setReloadTick] = useState(0);

  const canPublish = permissions.includes("reports:publish");
  const canManageShared = permissions.includes("reports:manage_shared");
  const usesTenantDefault = canManageShared;
  const canViewAllSaved = permissions.includes("reports:view_all_saved");

  const loadSavedData = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ enrich: "1" });
      if (query.trim()) params.set("q", query.trim());
      if (kind && kind !== "all") params.set("kind", kind);
      if (sort) params.set("sort", sort);
      if (direction) params.set("direction", direction);
      if (scope === "all") params.set("scope", "all");
      if (favoriteFilter === "favorites") {
        params.set("favorite", "1");
      }

      const resp = await fetch(`${apiBase}/api/saved?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": email || "",
          ...getAdminTenantHeader(),
        },
        signal,
        cache: "no-store",
      });

      if (!resp.ok) throw new Error(`saved ${resp.status}`);
      const data = await resp.json();
      setItems(data.items || []);
      const nextPermissions = data.permissions || [];
      setPermissions(nextPermissions);
      if (!nextPermissions.includes("reports:view_all_saved") && scope !== "mine") {
        setScope("mine");
      }
    },
    [
      accessToken,
      apiBase,
      direction,
      email,
      favoriteFilter,
      kind,
      query,
      scope,
      sort,
    ],
  );

  useEffect(() => {
    if (!accessToken) return;

    const controller = new AbortController();

    (async () => {
      try {
        await loadSavedData(controller.signal);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [accessToken, loadSavedData, reloadTick]);

  useEffect(() => {
    writeFilterMemory(userFilterKey, "saved-browser", {
      savedBrowser: {
        query,
        kind,
        sort,
        direction,
        scope,
        favoriteFilter,
      },
    });
  }, [direction, favoriteFilter, kind, query, scope, sort, userFilterKey]);

  const refresh = () => setReloadTick((v) => v + 1);

  const getName = (it: SavedItem) =>
    it.mb?.exists === false
      ? "(Deleted in Metabase)"
      : it.display_name || it.mb?.name || it.title;

  const getSavedMenuKey = (it: SavedItem) => `saved:${it.kind}:${it.id}`;

  const getEffectiveMenuSection = (it: SavedItem) =>
    String(
      it.has_personal_menu_assignment
        ? it.personal_menu_section || ""
        : it.personal_menu_section || it.published_menu_section || "",
    ).trim();

  const handleOpen = async (it: SavedItem) => {
    if (it.mb?.exists === false) return;

    const id = Number(it.metabase_id);
    if (!Number.isFinite(id)) return;

    try {
      await markSavedItemRun(accessToken, it.id, email || undefined);
    } catch {
      // Opening the report should not fail just because run tracking failed.
    }

    onOpen({
      id: it.id,
      kind: it.kind,
      metabase_id: id,
      title: getName(it),
      can_edit_saved: it.can_edit_saved !== false,
    });
  };

  const handleRename = async (it: SavedItem) => {
    const nextName = window.prompt("Rename", getName(it));
    if (nextName == null) return;

    await updateSavedItem(
      accessToken,
      it.id,
      { display_name: nextName.trim() || null },
      email || undefined,
    );
    refresh();
    await onPublished?.();
  };

  const handleDelete = async (it: SavedItem) => {
    if (!window.confirm(`Remove "${getName(it)}" from My Saved?`)) return;
    await deleteSavedItem(accessToken, it.id, email || undefined);
    refresh();
    await onPublished?.();
  };

  const handleSetFavorite = async (it: SavedItem, checked: boolean) => {
    const previousItems = items;
    setItems((current) =>
      current.map((item) =>
        item.id === it.id ? { ...item, is_favorite: checked } : item,
      ),
    );

    try {
      const saved = await setSavedFavorite(
        accessToken,
        it.id,
        checked,
        email || undefined,
      );
      setItems((current) =>
        current
          .map((item) =>
            item.id === it.id ? { ...item, ...saved } : item,
          )
          .filter((item) => favoriteFilter !== "favorites" || item.is_favorite),
      );
    } catch (e: any) {
      setItems(previousItems);
      setError(e?.message || "Failed to update favorite");
    }
  };

  const handleSetMenuTitle = async (it: SavedItem, menuTitle: string) => {
    const nextValue = menuTitle.trim();
    if (!it.can_edit_saved && !canManageShared) return;
    if (it.has_personal_menu_assignment && String(it.personal_menu_section || "").trim() === nextValue) return;

    setError(null);
    try {
      const saved = await setPersonalMenuAssignment(
        accessToken,
        it.id,
        nextValue || null,
        email || undefined,
      );
      setItems((current) =>
        current.map((item) =>
          item.id === it.id ? { ...item, ...saved, has_personal_menu_assignment: true } : item,
        ),
      );
      await onPublished?.();
    } catch (e: any) {
      setError(e?.message || "Failed to update the Menu Title");
    }
  };

  const getMenuTitleOptions = () => {
    const titles = Array.from(
      new Set(
        menuTitles
          .map((title) => title.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { value: "__none__", label: "Not in menu" },
      ...titles.map((title) => ({ value: title, label: title })),
    ];
  };

  const handlePublish = async (it: SavedItem) => {
    const menuSection = getEffectiveMenuSection(it);
    if (!menuSection) {
      setError("Select a Menu Title before publishing.");
      return;
    }

    const publishResult = await publishSavedItem(
      accessToken,
      it.id,
      {
        title: getName(it),
        menu_section: menuSection,
      },
      email || undefined,
    );
    // The backend returns the post-commit preview menu with the report. Using
    // it directly keeps the sidebar in sync even when a later GET is delayed.
    const publishedReport = publishResult?.report || publishResult;
    setItems((current) =>
      current.map((item) =>
        item.id === it.id
          ? {
              ...item,
              is_published: true,
              published_menu_section: menuSection,
            }
          : item,
      ),
    );
    refresh();
    await onPublished?.({ publishedReport, preview: publishResult?.preview || null });
  };

  const handleUnpublish = async (it: SavedItem) => {
    const hasPersonalShortcut = !!String(it.personal_menu_section || "").trim();
    const personalMessage = hasPersonalShortcut ? " and remove your personal menu shortcut" : "";
    if (!window.confirm(`Unpublish "${getName(it)}" from this tenant's menus${personalMessage}?`)) return;

    setError(null);
    setUnpublishWarning(null);
    try {
      // A saved resource may have older publications in several sections.
      // Remove every tenant publication so another section cannot reappear.
      const unpublishResult = await unpublishSavedItem(
        accessToken,
        it.id,
        {},
        email || undefined,
      );
      let personalShortcutCleared = false;
      let personalShortcutError: string | null = null;
      if (hasPersonalShortcut) {
        try {
          await setPersonalMenuAssignment(accessToken, it.id, null, email || undefined);
          personalShortcutCleared = true;
        } catch (e: any) {
          personalShortcutError = `Tenant publication removed, but your personal menu shortcut could not be removed: ${e?.message || String(e)}`;
        }
      }
      setItems((current) =>
        current.map((item) =>
          item.id === it.id
            ? {
                ...item,
                is_published: false,
                published_menu_section: null,
                ...(personalShortcutCleared
                  ? { personal_menu_section: null, has_personal_menu_assignment: true }
                  : {}),
              }
            : item,
        ),
      );
      refresh();
      await onPublished?.({ preview: unpublishResult?.preview || null });
      if (personalShortcutError) setUnpublishWarning(personalShortcutError);
    } catch (e: any) {
      setError(e?.message || "Failed to unpublish the saved item");
    }
  };

  const handleToggleDefault = async (it: SavedItem) => {
    if (usesTenantDefault) {
      const menuSection = getEffectiveMenuSection(it);
      if (!menuSection) {
        setError("Select a Menu Title before setting the tenant default.");
        return;
      }
      if (!it.is_published && !it.is_tenant_default) {
        setError("Publish this item to the selected role menu before setting it as the tenant default.");
        return;
      }

      setError(null);

      try {
        if (it.is_tenant_default) {
          await clearTenantDefaultSavedItem(
            accessToken,
            it.id,
            { menu_section: menuSection },
            email || undefined,
          );
          setItems((current) =>
            current.map((item) =>
              item.id === it.id ? { ...item, is_tenant_default: false } : item,
            ),
          );
        } else {
          const result = await setTenantDefaultSavedItem(
            accessToken,
            it.id,
            { menu_section: menuSection },
            email || undefined,
          );
          const targetKey =
            typeof result?.key === "string"
              ? result.key
              : typeof result?.report?.slug === "string"
                ? result.report.slug
                : undefined;
          setItems((current) =>
            current.map((item) => ({
              ...item,
              is_tenant_default: item.id === it.id,
              is_published: item.id === it.id ? true : item.is_published,
              published_menu_section:
                item.id === it.id ? menuSection : item.published_menu_section,
            })),
          );
          refresh();
          await onPublished?.({ openDefault: true, targetKey });
          return;
        }
      } catch (e: any) {
        setError(e?.message || "Failed to update tenant default");
        return;
      }

      refresh();
      await onPublished?.({ openDefault: true });
      return;
    }

    const menuKey = getSavedMenuKey(it);

    if (defaultMenuItemKey === menuKey) {
      await onClearDefault?.();
      return;
    }

    await onSetDefault?.(menuKey);
  };

  const rows = items.map((it) => {
      const disabled = it.mb?.exists === false;
      const name = getName(it);
      const menuKey = getSavedMenuKey(it);
      const canEditSaved = it.can_edit_saved !== false;
      const effectiveMenuSection = getEffectiveMenuSection(it);
      const isInMenu = !!effectiveMenuSection;
      const isDefault = defaultMenuItemKey === menuKey;

      const lastEdited = it.mb?.updated_at || it.updated_at;

      return (
        <Table.Tr
          key={it.id}
          className={[
            "rt-row-clickable",
            disabled ? "rt-row-disabled" : "",
          ].join(" ")}
          onClick={() => handleOpen(it)}
        >
          <Table.Td w={180}>
            <Group gap="xs">
              <Badge variant="light" className="rt-badge">
                {it.kind === "card" ? "REPORT" : "DASHBOARD/COMPLEX REPORT"}
              </Badge>
              {it.mb?.archived ? (
                <Badge color="gray" variant="light" className="rt-badge" data-archived="true">
                  ARCHIVED
                </Badge>
              ) : null}
            </Group>
          </Table.Td>

          <Table.Td>
            <Text fw={700} className="rt-text-strong">
              {name}
            </Text>
            <Text size="xs" className="rt-text-dim">
              #{it.metabase_id}
              {it.created_by_email ? ` - ${it.created_by_email}` : ""}
            </Text>
          </Table.Td>

          <Table.Td w={260}>
            <Text size="sm" className="rt-text">
              {lastEdited ? new Date(lastEdited).toLocaleString() : "-"}
            </Text>
          </Table.Td>

          <Table.Td w={220}>
            <Text size="sm" className="rt-text">
              {it.last_run_at ? new Date(it.last_run_at).toLocaleString() : "-"}
            </Text>
          </Table.Td>

          <Table.Td w={110} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={!!it.is_favorite}
              disabled={!canEditSaved}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                canEditSaved && handleSetFavorite(it, e.currentTarget.checked)
              }
              aria-label={`Favorite ${name}`}
              title={
                canEditSaved
                  ? undefined
                  : "Only the creator can change this favorite"
              }
            />
          </Table.Td>

          <Table.Td w={220} onClick={(e) => e.stopPropagation()}>
            <Select
              key={`${it.id}-${effectiveMenuSection}`}
              size="xs"
              value={effectiveMenuSection || "__none__"}
              data={getMenuTitleOptions()}
              disabled={!canEditSaved && !usesTenantDefault}
              onChange={(value) =>
                handleSetMenuTitle(it, value && value !== "__none__" ? value : "")
              }
              comboboxProps={{ withinPortal: true }}
              title={
                canEditSaved || usesTenantDefault
                  ? undefined
                  : "Only the creator can change the Menu Title"
              }
            />
          </Table.Td>

          <Table.Td w={150} onClick={(e) => e.stopPropagation()}>
            <Button
              size="xs"
              variant={
                usesTenantDefault
                  ? it.is_tenant_default
                    ? "filled"
                    : "light"
                  : isDefault
                    ? "filled"
                    : "light"
              }
              disabled={
                !isInMenu ||
                (usesTenantDefault && !it.is_published && !it.is_tenant_default) ||
                (!usesTenantDefault && !canEditSaved)
              }
              onClick={() => handleToggleDefault(it)}
              title={
                !isInMenu
                  ? "Select a Menu Title before setting a default"
                  : usesTenantDefault && !it.is_published && !it.is_tenant_default
                    ? "Publish this item to the selected role menu before setting a tenant default"
                  : !usesTenantDefault && !canEditSaved
                    ? "Only the creator can set this as a personal default"
                    : usesTenantDefault && it.is_tenant_default
                      ? "Revert to the previous tenant default"
                    : undefined
              }
            >
              {usesTenantDefault
                ? it.is_tenant_default
                  ? "Clear tenant default"
                  : "Set tenant default"
                : isDefault
                  ? "Default"
                  : "Set default"}
            </Button>
          </Table.Td>

          <Table.Td w={280} onClick={(e) => e.stopPropagation()}>
            <Group gap="xs" wrap="wrap">
              <Button
                size="xs"
                variant="light"
                disabled={!canEditSaved}
                onClick={() => canEditSaved && handleRename(it)}
                title={
                  canEditSaved
                    ? undefined
                    : "Only the creator can rename this saved item"
                }
              >
                Rename
              </Button>
              <Button
                size="xs"
                variant="light"
                color="red"
                disabled={!canEditSaved}
                onClick={() => canEditSaved && handleDelete(it)}
                title={
                  canEditSaved
                    ? undefined
                    : "Only the creator can delete this saved item"
                }
              >
                Delete
              </Button>
              {canPublish ? (
                it.is_published ? (
                  <>
                    {usesTenantDefault && (
                      <Button
                        size="xs"
                        variant="light"
                        disabled={!isInMenu}
                        onClick={() => handlePublish(it)}
                        title="Ensure this saved item is published to all users in this tenant"
                      >
                        Make tenant-wide
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => handleUnpublish(it)}
                      title={
                        usesTenantDefault
                          ? "Unpublish this saved item from all users in this tenant"
                          : "Unpublish this saved item from your menu"
                      }
                    >
                      Unpublish
                    </Button>
                  </>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    disabled={!isInMenu}
                    onClick={() => handlePublish(it)}
                    title={
                      isInMenu
                        ? usesTenantDefault
                          ? "Publish this saved item to all users in this tenant"
                          : undefined
                        : "Select a Menu Title before publishing"
                    }
                  >
                    Publish to menu
                  </Button>
                )
              ) : null}
            </Group>
          </Table.Td>
        </Table.Tr>
      );
    });

  return (
    <Container fluid w="100%" p={20} className="rt-saved-wrap">
      {administrativeTenantLabel && (
        <Paper className="rt-admin-saved-context" withBorder p="sm" mb="md">
          <Group justify="space-between" gap="sm">
            <Text size="sm">
              Viewing saved items for <strong>{administrativeTenantLabel}</strong> in Company Administrator context.
            </Text>
            <Button size="xs" variant="subtle" onClick={onExitAdministrativeTenant}>
              Return to my tenant
            </Button>
          </Group>
        </Paper>
      )}
      <Flex justify="space-between" gap="xs" pb="xs" wrap="wrap">
        <Group gap="xs" wrap="wrap">
          <TextInput
            size="sm"
            placeholder="Search saved reports"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <Select
            size="sm"
            w={150}
            value={kind}
            onChange={setKind}
            data={[
              { value: "all", label: "All types" },
              { value: "card", label: "Reports" },
              { value: "dashboard", label: "Dashboards" },
            ]}
          />
          <Select
            size="sm"
            w={150}
            value={favoriteFilter}
            onChange={setFavoriteFilter}
            data={[
              { value: "all", label: "All saved" },
              { value: "favorites", label: "Favorites" },
            ]}
          />
          <Select
            size="sm"
            w={150}
            value={sort}
            onChange={setSort}
            data={[
              { value: "updated_at", label: "Last edited" },
              { value: "last_run_at", label: "Last run" },
              { value: "name", label: "Name" },
              { value: "kind", label: "Type" },
              { value: "created_at", label: "Created" },
            ]}
          />
          <Select
            size="sm"
            w={130}
            value={direction}
            onChange={setDirection}
            data={[
              { value: "desc", label: "Desc" },
              { value: "asc", label: "Asc" },
            ]}
          />
          {canViewAllSaved ? (
            <Select
              size="sm"
              w={140}
              value={scope}
              onChange={setScope}
              data={[
                { value: "mine", label: "Mine" },
                { value: "all", label: "All users" },
              ]}
            />
          ) : null}
        </Group>

        <Group gap="xs">
        {canCreateSql ? (
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <Button className="rt-btn-primary">New Report</Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => onNewQuestion("model")}>From Model</Menu.Item>
              <Menu.Item onClick={() => onNewQuestion("sql")}>From SQL</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Button className="rt-btn-primary" onClick={() => onNewQuestion("model")}>
            New Report
          </Button>
        )}
        <Button className="rt-btn-primary" onClick={onNewDashboard}>
          New Dashboard
        </Button>
        </Group>
      </Flex>

      {unpublishWarning && <Text c="orange">{unpublishWarning}</Text>}
      {error && !loading ? (
        <Alert
          color="red"
          title="Unable to complete that action"
          mb="md"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Group justify="center" py="xl">
          <Loader color="var(--primary)" />
        </Group>
      ) : (
        <Paper className="rt-panel rt-saved-panel" radius="md" p="md">
          <Table className="rt-table" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Last edited at</Table.Th>
                <Table.Th>Last run date</Table.Th>
                <Table.Th>Favorite</Table.Th>
                <Table.Th>Menu Title</Table.Th>
                <Table.Th>Default</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Paper>
      )}
    </Container>
  );
}
